import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import axios from 'axios';
import { 
  Mail, 
  Plus, 
  Pencil, 
  Trash2, 
  X, 
  Check,
  Eye,
  Send,
  Search,
  Filter,
  Copy,
  Clock,
  FileText,
  Users,
  Building,
  Store,
  ChevronRight,
  Save,
  RefreshCw,
  Code,
  Layers
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Axios instance with auth header
const getAuthConfig = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
});

const TEMPLATE_TYPES = [
  { value: 'B2C', label: 'B2C', icon: Users, color: 'bg-blue-500', description: 'Customer communications' },
  { value: 'B2B', label: 'B2B', icon: Building, color: 'bg-purple-500', description: 'Business partner communications' },
  { value: 'B2R', label: 'B2R', icon: Store, color: 'bg-orange-500', description: 'Retailer communications' },
  { value: 'Internal', label: 'Internal', icon: FileText, color: 'bg-gray-500', description: 'Internal notifications' },
];

const STATUS_OPTIONS = [
  { value: 'active', label: 'Active', color: 'bg-green-100 text-green-800' },
  { value: 'draft', label: 'Draft', color: 'bg-yellow-100 text-yellow-800' },
  { value: 'archived', label: 'Archived', color: 'bg-gray-100 text-gray-800' },
];

const CATEGORIES = {
  B2C: [
    { value: 'order', label: 'Order' },
    { value: 'payment', label: 'Payment' },
    { value: 'delivery', label: 'Delivery' },
    { value: 'lead', label: 'Lead' },
    { value: 'support', label: 'Support' },
    { value: 'promotional', label: 'Promotional' },
  ],
  B2B: [
    { value: 'quote', label: 'Quote' },
    { value: 'invoice', label: 'Invoice' },
    { value: 'partner', label: 'Partner' },
  ],
  B2R: [
    { value: 'order', label: 'Order' },
    { value: 'payout', label: 'Payout' },
    { value: 'performance', label: 'Performance' },
    { value: 'onboarding', label: 'Onboarding' },
  ],
  Internal: [
    { value: 'hr', label: 'HR' },
    { value: 'notification', label: 'Notification' },
  ]
};

function EmailTemplates({ user, onLogout }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedType, setSelectedType] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [typeCounts, setTypeCounts] = useState({});
  
  // Editor state
  const [showEditor, setShowEditor] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewSubject, setPreviewSubject] = useState('');
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    type: 'B2C',
    category: 'order',
    subject: '',
    body_html: '',
    description: '',
    status: 'draft',
    variables: []
  });
  
  // Test email state
  const [showTestModal, setShowTestModal] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [sendingTest, setSendingTest] = useState(false);

  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (selectedType !== 'all') params.type = selectedType;
      if (searchQuery) params.search = searchQuery;
      
      const response = await axios.get(`${API}/api/email-templates`, { 
        params,
        ...getAuthConfig()
      });
      setTemplates(response.data.templates || []);
      setTypeCounts(response.data.type_counts || {});
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
    setLoading(false);
  }, [selectedType, searchQuery]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const handleCreateNew = () => {
    setEditingTemplate(null);
    setFormData({
      name: '',
      slug: '',
      type: 'B2C',
      category: 'order',
      subject: '',
      body_html: getDefaultBodyTemplate(),
      description: '',
      status: 'draft',
      variables: []
    });
    setPreviewHtml('');
    setPreviewSubject('');
    setShowEditor(true);
  };

  const handleEdit = (template) => {
    setEditingTemplate(template);
    setFormData({
      name: template.name,
      slug: template.slug,
      type: template.type,
      category: template.category,
      subject: template.subject,
      body_html: template.body_html,
      description: template.description || '',
      status: template.status,
      variables: template.variables || []
    });
    generatePreview(template.subject, template.body_html, template.variables || []);
    setShowEditor(true);
  };

  const handleDelete = async (templateId) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;
    
    try {
      await axios.delete(`${API}/api/email-templates/${templateId}`, getAuthConfig());
      fetchTemplates();
    } catch (error) {
      console.error('Error deleting template:', error);
      alert('Failed to delete template');
    }
  };

  const handleSave = async () => {
    if (!formData.name || !formData.slug || !formData.subject || !formData.body_html) {
      alert('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      if (editingTemplate) {
        await axios.put(`${API}/api/email-templates/${editingTemplate.id}`, {
          name: formData.name,
          subject: formData.subject,
          body_html: formData.body_html,
          description: formData.description,
          status: formData.status,
          variables: formData.variables
        }, getAuthConfig());
      } else {
        await axios.post(`${API}/api/email-templates`, formData, getAuthConfig());
      }
      setShowEditor(false);
      fetchTemplates();
    } catch (error) {
      console.error('Error saving template:', error);
      alert(error.response?.data?.detail || 'Failed to save template');
    }
    setLoading(false);
  };

  const generatePreview = async (subject, body, variables) => {
    // Replace variables with sample values
    let previewBody = body;
    let previewSub = subject;
    
    (variables || []).forEach(v => {
      const placeholder = `{{${v.name}}}`;
      const value = v.sample_value || `[${v.name}]`;
      previewBody = previewBody.split(placeholder).join(value);
      previewSub = previewSub.split(placeholder).join(value);
    });
    
    // Wrap with Zobox branding
    const wrappedHtml = wrapWithBranding(previewBody);
    setPreviewHtml(wrappedHtml);
    setPreviewSubject(previewSub);
  };

  const wrapWithBranding = (body) => {
    return `
      <html>
      <body style="font-family: Arial, sans-serif; margin: 0; padding: 0; background: #f9f9f9;">
        <div style="background: linear-gradient(135deg, #2b3380 0%, #4a5fc1 100%); padding: 30px; text-align: center;">
          <h1 style="color: white; margin: 0; font-size: 28px;">ZOBOX</h1>
          <p style="color: rgba(255,255,255,0.9); margin: 5px 0 0 0; font-size: 14px;">India's Leading Refurbished Device Network</p>
        </div>
        <div style="padding: 30px; background: white; margin: 20px auto; max-width: 600px;">
          ${body}
        </div>
        <div style="background: #f5f5f5; padding: 20px; text-align: center; margin-top: 30px;">
          <p style="color: #666; font-size: 12px; margin: 0;">© 2025 Zobox Protech Limited. All rights reserved.</p>
          <p style="color: #666; font-size: 12px; margin: 5px 0 0 0;">📧 support@zobox.in | 📞 +91 91111 77118 | 🌐 zobox.in</p>
        </div>
      </body>
      </html>
    `;
  };

  const getDefaultBodyTemplate = () => {
    return `<h2 style="color: #2b3380;">Your Email Title</h2>
<p style="color: #333; font-size: 16px;">Hi {{customer_name}},</p>
<p style="color: #666; line-height: 1.6;">
  Your email content goes here. Use variables like {{variable_name}} for dynamic content.
</p>

<div style="background: #f5f5f5; padding: 20px; border-radius: 8px; margin: 20px 0;">
  <h3 style="color: #2b3380; margin-top: 0;">Section Title</h3>
  <p style="margin: 5px 0;"><strong>Field:</strong> {{field_value}}</p>
</div>

<div style="text-align: center; margin: 25px 0;">
  <a href="{{action_url}}" 
     style="display: inline-block; background: #2b3380; color: white; padding: 12px 30px; 
            text-decoration: none; border-radius: 5px;">
    Call to Action
  </a>
</div>`;
  };

  const handleSendTest = async () => {
    if (!testEmail || !editingTemplate) return;
    
    setSendingTest(true);
    try {
      await axios.post(`${API}/api/email-templates/${editingTemplate.id}/send-test`, {
        to_email: testEmail,
        sample_data: {}
      }, getAuthConfig());
      alert(`Test email sent to ${testEmail}`);
      setShowTestModal(false);
    } catch (error) {
      console.error('Error sending test email:', error);
      alert(error.response?.data?.detail || 'Failed to send test email');
    }
    setSendingTest(false);
  };

  const seedDefaultTemplates = async () => {
    if (!window.confirm('This will add default templates. Continue?')) return;
    
    try {
      const response = await axios.post(`${API}/api/email-templates/seed-defaults`, {}, getAuthConfig());
      alert(response.data.message);
      fetchTemplates();
    } catch (error) {
      console.error('Error seeding templates:', error);
      alert('Failed to seed templates');
    }
  };

  const addVariable = () => {
    setFormData({
      ...formData,
      variables: [...formData.variables, { name: '', description: '', sample_value: '' }]
    });
  };

  const updateVariable = (index, field, value) => {
    const newVars = [...formData.variables];
    newVars[index][field] = value;
    setFormData({ ...formData, variables: newVars });
  };

  const removeVariable = (index) => {
    const newVars = formData.variables.filter((_, i) => i !== index);
    setFormData({ ...formData, variables: newVars });
  };

  const extractVariablesFromBody = () => {
    const regex = /\{\{(\w+)\}\}/g;
    const matches = [...formData.body_html.matchAll(regex), ...formData.subject.matchAll(regex)];
    const uniqueVars = [...new Set(matches.map(m => m[1]))];
    
    const existingNames = formData.variables.map(v => v.name);
    const newVars = uniqueVars
      .filter(name => !existingNames.includes(name))
      .map(name => ({ name, description: '', sample_value: '' }));
    
    if (newVars.length > 0) {
      setFormData({
        ...formData,
        variables: [...formData.variables, ...newVars]
      });
    }
  };

  // Template List View
  const renderTemplateList = () => (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Email Templates</h1>
          <p className="text-gray-500 mt-1">Manage email templates for B2C, B2B, and B2R communications</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={seedDefaultTemplates}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 flex items-center gap-2"
          >
            <RefreshCw size={16} />
            Seed Defaults
          </button>
          <button
            onClick={handleCreateNew}
            className="px-4 py-2 bg-[#2B3380] text-white rounded-lg hover:bg-[#3a45a0] flex items-center gap-2"
          >
            <Plus size={16} />
            New Template
          </button>
        </div>
      </div>

      {/* Type Tabs */}
      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => setSelectedType('all')}
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
            selectedType === 'all' 
              ? 'bg-[#2B3380] text-white' 
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          All ({Object.values(typeCounts).reduce((a, b) => a + b, 0)})
        </button>
        {TEMPLATE_TYPES.map(type => {
          const Icon = type.icon;
          return (
            <button
              key={type.value}
              onClick={() => setSelectedType(type.value)}
              className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
                selectedType === type.value 
                  ? 'bg-[#2B3380] text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <Icon size={16} />
              {type.label} ({typeCounts[type.value] || 0})
            </button>
          );
        })}
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
        <input
          type="text"
          placeholder="Search templates..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#2B3380] focus:border-transparent"
        />
      </div>

      {/* Templates Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-6 py-4 text-sm font-semibold text-gray-600">Template</th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-gray-600">Type</th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-gray-600">Category</th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-gray-600">Status</th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-gray-600">Last Updated</th>
                <th className="text-right px-6 py-4 text-sm font-semibold text-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                    <RefreshCw className="animate-spin mx-auto mb-2" size={24} />
                    Loading templates...
                  </td>
                </tr>
              ) : templates.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                    <Mail className="mx-auto mb-2 text-gray-300" size={48} />
                    <p className="text-lg font-medium">No templates found</p>
                    <p className="text-sm">Create a new template or seed defaults to get started</p>
                  </td>
                </tr>
              ) : templates.map(template => {
                const typeInfo = TEMPLATE_TYPES.find(t => t.value === template.type);
                const statusInfo = STATUS_OPTIONS.find(s => s.value === template.status);
                const TypeIcon = typeInfo?.icon || Mail;
                
                return (
                  <tr key={template.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div>
                        <p className="font-medium text-gray-900">{template.name}</p>
                        <p className="text-sm text-gray-500 truncate max-w-xs">{template.subject}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${typeInfo?.color} text-white`}>
                        <TypeIcon size={12} />
                        {template.type}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="capitalize text-gray-600">{template.category}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${statusInfo?.color}`}>
                        {template.status === 'active' && <Check size={12} className="mr-1" />}
                        {statusInfo?.label}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {template.updated_at ? new Date(template.updated_at).toLocaleDateString() : '-'}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => handleEdit(template)}
                          className="p-2 text-gray-600 hover:text-[#2B3380] hover:bg-gray-100 rounded-lg"
                          title="Edit"
                        >
                          <Pencil size={16} />
                        </button>
                        <button
                          onClick={() => {
                            setEditingTemplate(template);
                            generatePreview(template.subject, template.body_html, template.variables);
                          }}
                          className="p-2 text-gray-600 hover:text-[#2B3380] hover:bg-gray-100 rounded-lg"
                          title="Preview"
                        >
                          <Eye size={16} />
                        </button>
                        <button
                          onClick={() => handleDelete(template.id)}
                          className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg"
                          title="Delete"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  // Editor View (Split Panel)
  const renderEditor = () => (
    <div className="h-[calc(100vh-120px)] flex flex-col">
      {/* Editor Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
        <div className="flex items-center gap-4">
          <button
            onClick={() => setShowEditor(false)}
            className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg"
          >
            <X size={20} />
          </button>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              {editingTemplate ? 'Edit Template' : 'New Template'}
            </h2>
            <p className="text-sm text-gray-500">{formData.name || 'Untitled'}</p>
          </div>
        </div>
        <div className="flex gap-2">
          {editingTemplate && (
            <button
              onClick={() => setShowTestModal(true)}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 flex items-center gap-2"
            >
              <Send size={16} />
              Send Test
            </button>
          )}
          <button
            onClick={handleSave}
            disabled={loading}
            className="px-4 py-2 bg-[#2B3380] text-white rounded-lg hover:bg-[#3a45a0] flex items-center gap-2 disabled:opacity-50"
          >
            <Save size={16} />
            {loading ? 'Saving...' : 'Save Template'}
          </button>
        </div>
      </div>

      {/* Split Panel */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Panel - Form */}
        <div className="w-1/2 border-r border-gray-200 overflow-y-auto p-6 bg-gray-50">
          <div className="space-y-6 max-w-xl">
            {/* Basic Info */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <FileText size={18} />
                Basic Information
              </h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Template Name *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., Order Confirmation"
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#2B3380] focus:border-transparent"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Slug *</label>
                  <input
                    type="text"
                    value={formData.slug}
                    onChange={(e) => setFormData({ ...formData, slug: e.target.value.toLowerCase().replace(/\s+/g, '-') })}
                    placeholder="e.g., b2c-order-confirmation"
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#2B3380] focus:border-transparent"
                    disabled={!!editingTemplate}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Type *</label>
                    <select
                      value={formData.type}
                      onChange={(e) => setFormData({ ...formData, type: e.target.value, category: CATEGORIES[e.target.value]?.[0]?.value || '' })}
                      className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#2B3380] focus:border-transparent"
                      disabled={!!editingTemplate}
                    >
                      {TEMPLATE_TYPES.map(t => (
                        <option key={t.value} value={t.value}>{t.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Category *</label>
                    <select
                      value={formData.category}
                      onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#2B3380] focus:border-transparent"
                      disabled={!!editingTemplate}
                    >
                      {(CATEGORIES[formData.type] || []).map(c => (
                        <option key={c.value} value={c.value}>{c.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#2B3380] focus:border-transparent"
                  >
                    {STATUS_OPTIONS.map(s => (
                      <option key={s.value} value={s.value}>{s.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <input
                    type="text"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="When is this template used?"
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#2B3380] focus:border-transparent"
                  />
                </div>
              </div>
            </div>

            {/* Email Content */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Mail size={18} />
                Email Content
              </h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Subject Line *</label>
                  <input
                    type="text"
                    value={formData.subject}
                    onChange={(e) => {
                      setFormData({ ...formData, subject: e.target.value });
                      generatePreview(e.target.value, formData.body_html, formData.variables);
                    }}
                    placeholder="e.g., Order Confirmed! 🎉 Your Order #{{order_id}}"
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#2B3380] focus:border-transparent"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Body (HTML) *</label>
                  <textarea
                    value={formData.body_html}
                    onChange={(e) => {
                      setFormData({ ...formData, body_html: e.target.value });
                      generatePreview(formData.subject, e.target.value, formData.variables);
                    }}
                    rows={12}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#2B3380] focus:border-transparent font-mono text-sm"
                    placeholder="<h2>Your email content...</h2>"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Use {"{{variable_name}}"} for dynamic content. Zobox header/footer will be added automatically.
                  </p>
                </div>
              </div>
            </div>

            {/* Variables */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                  <Code size={18} />
                  Variables
                </h3>
                <div className="flex gap-2">
                  <button
                    onClick={extractVariablesFromBody}
                    className="text-sm text-[#2B3380] hover:underline"
                  >
                    Auto-detect
                  </button>
                  <button
                    onClick={addVariable}
                    className="text-sm bg-gray-100 px-3 py-1 rounded hover:bg-gray-200"
                  >
                    + Add
                  </button>
                </div>
              </div>
              
              {formData.variables.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-4">
                  No variables defined. Click "Auto-detect" to extract from content.
                </p>
              ) : (
                <div className="space-y-3">
                  {formData.variables.map((v, idx) => (
                    <div key={idx} className="flex gap-2 items-start">
                      <input
                        type="text"
                        value={v.name}
                        onChange={(e) => updateVariable(idx, 'name', e.target.value)}
                        placeholder="Variable name"
                        className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm"
                      />
                      <input
                        type="text"
                        value={v.sample_value}
                        onChange={(e) => updateVariable(idx, 'sample_value', e.target.value)}
                        placeholder="Sample value"
                        className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm"
                      />
                      <button
                        onClick={() => removeVariable(idx)}
                        className="p-2 text-red-500 hover:bg-red-50 rounded"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right Panel - Preview */}
        <div className="w-1/2 bg-gray-100 overflow-y-auto p-6">
          <div className="sticky top-0 bg-gray-100 pb-4">
            <h3 className="font-semibold text-gray-900 flex items-center gap-2 mb-2">
              <Eye size={18} />
              Live Preview
            </h3>
            {previewSubject && (
              <div className="bg-white rounded-lg p-3 mb-4 border border-gray-200">
                <p className="text-xs text-gray-500">Subject</p>
                <p className="font-medium text-gray-900">{previewSubject}</p>
              </div>
            )}
          </div>
          
          <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-200">
            {previewHtml ? (
              <iframe
                srcDoc={previewHtml}
                title="Email Preview"
                className="w-full h-[600px] border-0"
              />
            ) : (
              <div className="h-[600px] flex items-center justify-center text-gray-400">
                <div className="text-center">
                  <Eye size={48} className="mx-auto mb-2 opacity-50" />
                  <p>Enter content to see preview</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  // Test Email Modal
  const renderTestModal = () => (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl p-6 w-full max-w-md shadow-xl">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Send Test Email</h3>
        <p className="text-sm text-gray-500 mb-4">
          Send a test email with sample data to verify the template.
        </p>
        <input
          type="email"
          value={testEmail}
          onChange={(e) => setTestEmail(e.target.value)}
          placeholder="Enter email address"
          className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#2B3380] focus:border-transparent mb-4"
        />
        <div className="flex justify-end gap-2">
          <button
            onClick={() => setShowTestModal(false)}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
          >
            Cancel
          </button>
          <button
            onClick={handleSendTest}
            disabled={sendingTest || !testEmail}
            className="px-4 py-2 bg-[#2B3380] text-white rounded-lg hover:bg-[#3a45a0] disabled:opacity-50 flex items-center gap-2"
          >
            <Send size={16} />
            {sendingTest ? 'Sending...' : 'Send Test'}
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-6">
        {showEditor ? renderEditor() : renderTemplateList()}
        {showTestModal && renderTestModal()}
      </div>
    </Layout>
  );
}

export default EmailTemplates;
